import React from 'react'
import { isNull } from 'lodash'
import { format } from 'date-fns'
import { useToaster } from 'utils/useToaster'
import { useHistory, useParams } from 'react-router-dom'
import {
  getFormattedDateAndTime,
  getResultLabelColor,
  TestSessionWithoutMembersWithResult_testSession,
  useAuth,
  UserRole,
  GroupMemberRole,
  CompanyMemberRole,
  getFormattedAddress,
  useTestSessionMembers,
  useStateWithDebounce,
  useTestSessionMembersQuickReport,
  SortByType,
  useRemoveTestSessionMember,
  useTestSessionWithoutMembers,
  TestSessionMembers_testSessionMembers_testSessionMembers_User,
  TestSessionMembers_testSessionMembers_testSessionMembers_Group,
  UserPermissions,
  useDeleteMembers,
  useSubperson,
  TestSessionPaymentStatus,
  TestResult,
} from '@modmd/data'
import { COLOR, DEVICE, pxToRem } from 'theme'
import { isInfluenza, useSortChangeHandler } from 'utils/helpers'
import { useSearchState } from 'internal-portal/utils/useSearchState'
import { useIsMinDevice } from 'utils/hooks/useMedia'
import { ShowUserCode } from 'sharedComponents/ShowUserCode'
import Datalist from 'components/Datalist'
import { Details } from 'sharedComponents/Details'
import { Loading } from 'components/Loading'
import { AddNewTestSessionMembers } from 'sharedComponents/AddNewTestSessionMembers'
import { DownloadTestResultPdfButton } from 'sharedComponents/DownloadTestResultPdfButton'
import { TestSessionPaymentLabel } from 'sharedComponents/TestSessionPaymentLabel'
import { SessionPaymentDialog } from 'sharedComponents/SessionPaymentDialog'
import Card from 'components/Card'
import { Spinner } from 'components/Spinner'
import { SelectInput } from 'components/SelectInput'
import TextInput from 'components/TextInput'
import Pagination from 'components/Pagination'
import { Text } from 'components/Typography'
import Button from 'components/Button'
import Icon from 'components/Icon/Icon'
import Label from 'components/Label'
import { OrderedProductsInfo } from 'sharedComponents/OrderedProductsInfo'
import { COMPANY_ROUTES, ROUTES } from 'client-portal/constants/routes'
import { CsvDownloadButton } from 'sharedComponents/CsvDownloadButton'
import { InsuranceCountDownloadButton } from 'sharedComponents/InsuranceCountDownloadButton'
import { Box } from 'components/Layout'
import { Checkbox } from 'components/Checkbox'
import { ConfirmationDialog } from 'components/Dialog'
import { EditSession } from './EditTestSession'

interface Params {
  companyId: string
}

enum MemberType {
  staff = 'staff',
  participant = 'participant',
}

interface ColumnLabels {
  date?: string | null
  location?: string | null
  numOfPeople?: string | null
  status?: string | null
  paymentStatus?: string | null
}

interface Props {
  goBackPath: string
  testSessionId: string
  isEditable?: boolean
  isAddable?: boolean
  userId?: string
  skipTest?: boolean
  onlyPermitted?: boolean
  groupIds?: Array<string>
  staffOnly?: boolean
  participantOnly?: boolean
  columnLabels?: ColumnLabels
  isOrderVisible?: boolean
  isDownloadCsvEnabled?: boolean
}

enum TestedType {
  ALL = '',
  TESTED_YET = 'testedYet',
  NOT_TESTED_YET = 'NotTestedYet',
}

export enum UrlSearchQuery {
  EMAIL_OR_NAME_LIKE = 'emailOrNameLike',
  PAGE = 'page',
  TESTED = 'tested',
}

const defaultColumnLabels = {
  date: 'Date',
  location: 'Location',
  numOfPeople: 'Number of people',
  status: 'Status',
  paymentStatus: 'Payment',
}
export const SessionDetail: React.FC<Props> = ({
  testSessionId,
  goBackPath,
  isEditable,
  isAddable,
  userId,
  skipTest,
  onlyPermitted,
  groupIds,
  staffOnly,
  participantOnly,
  columnLabels: providedColumnLabels,
  isOrderVisible = false,
  isDownloadCsvEnabled = false,
}) => {
  const history = useHistory()
  const params = useParams<Params>()
  const isDesktop = useIsMinDevice(DEVICE.DESKTOP)
  const isMobile = !useIsMinDevice(DEVICE.TABLET)
  const [isDialogOpen, setIsDialogOpen] = React.useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = React.useState(false)
  const [isRefetchingSessionMembers, setIsRefetchingSessionMembers] = React.useState(false)
  const [rowId, setRowId] = React.useState<string | null>(null)
  const [selected, setSelected] = React.useState<string[]>([])
  const [allSelected, setAllSelected] = React.useState<boolean>(false)
  const [memberSelected, setMemberSelected] = React.useState<{
    member: keyof typeof MemberType
    name: string
    id: string
  } | null>(null)
  const [confirmDeleteMember, setConfirmDeleteMember] = React.useState(false)
  const [confirmDeletePerson, setConfirmDeletePerson] = React.useState(false)

  const { setToastMessage } = useToaster()
  const searchState = useSearchState<{
    [UrlSearchQuery.EMAIL_OR_NAME_LIKE]?: string
    [UrlSearchQuery.PAGE]?: string
    [UrlSearchQuery.TESTED]?: TestedType
  }>()

  const { data: subpersonData } = useSubperson({ userId: userId || '', skip: !userId })

  const [
    removeTestSessionMember,
    { loading: isLoadingRemoveTestSessionMember },
  ] = useRemoveTestSessionMember()

  const [emailOrNameLike, setEmailOrNameLike] = useStateWithDebounce(
    searchState.data.emailOrNameLike,
    (nameLike) => {
      searchState.setSearchState(
        { emailOrNameLike: nameLike, page: nameLike ? 1 : searchState.data.page },
        false,
        true
      )
    }
  )

  const {
    data: testSessionDetail,
    loading: isSessionDetailLoading,
    refetch: refetchSessionDetail,
  } = useTestSessionWithoutMembers({
    testSessionId,
    skipResult: false,
    userId,
  })

  const { sorter, handleSortChange } = useSortChangeHandler()
  const {
    data: sessionMembers,
    loading: areSessionMembersLoading,
    refetch: refetchSessionMembers,
  } = useTestSessionMembers({
    userId,
    testSessionId,
    emailOrNameLike: searchState.data.emailOrNameLike,
    hasTest: searchState.data.tested
      ? searchState.data.tested === TestedType.TESTED_YET
      : undefined,
    groupIds,
    skipTest,
    onlyPermitted,
    staffOnly,
    participantOnly,
    page: Number(searchState.data.page ?? 1),
    sortBy: sorter?.sortBy,
    direction: sorter?.direction,
  })

  const patientsNotTested = React.useMemo(
    () =>
      sessionMembers?.testSessionMembers.testSessionMembers.filter(
        ({ isParticipant, Test }) => isParticipant && !Test?.testedAt
      ) || [],
    [sessionMembers]
  )

  const MemberCount = React.useMemo(
    () => Number(sessionMembers?.testSessionMembers?.pagination.total) || 0,
    [sessionMembers?.testSessionMembers]
  )

  const [deleteMember, { loading: isLoading }] = useDeleteMembers({
    onCompleted: () => {
      searchState.setSearchState({ page: 1 }, false)
      setSelected([])
      setAllSelected(false)
      void refetchSessionMembers()
      setConfirmDeletePerson(false)
    },
  })

  const numberOfPeople = React.useMemo(
    () => sessionMembers?.testSessionMembers.pagination.total || 0,
    [sessionMembers?.testSessionMembers.pagination.total]
  )

  const {
    data: userData,
    getUserCompaniesWithRole,
    getUserGroupsWithRole,
    hasPermission,
    hasRoles,
  } = useAuth()
  const { hasCompaniesWithRole } = getUserCompaniesWithRole({
    roles: [CompanyMemberRole.Manager, CompanyMemberRole.Owner],
    companyIds: [testSessionDetail?.testSession?.Company?.id || ''],
  })

  const getGroupsId = () => {
    if (testSessionDetail?.testSession?.Group?.id)
      return [testSessionDetail?.testSession?.Group?.id]
    if (testSessionDetail?.testSession?.Groups.length)
      return testSessionDetail?.testSession?.Groups.map((group) => group.id)
    return undefined
  }

  const { hasGroupsWithRole } = getUserGroupsWithRole({
    roles: [GroupMemberRole.Manager, GroupMemberRole.Coordinator],
    groupIds: getGroupsId(),
  })

  const groups = userData.User.GroupsWithRole.filter(
    (group) => group.role === GroupMemberRole.Manager || GroupMemberRole.Coordinator
  )
  const isManagerInGroup = groups.length > 0
  const isSessionFinished = Boolean(testSessionDetail?.testSession?.finishedAt)
  const isSessionScheduled = Boolean(testSessionDetail?.testSession?.isScheduled)
  const testSessionName = testSessionDetail?.testSession?.name ?? ''

  const canDownloadCsv =
    (isManagerInGroup || hasCompaniesWithRole) && isDownloadCsvEnabled && isSessionFinished

  const columnLabels = { ...defaultColumnLabels, ...(providedColumnLabels || {}) }
  const numOfColumns = Object.values(columnLabels).filter((label) => label !== null).length
  const rowGridTemplateColumns = React.useMemo(() => `repeat(${numOfColumns}, 1fr)`, [numOfColumns])

  const { date: sessionStartDate, endsAt: sessionEndDate } = React.useMemo(
    () => testSessionDetail?.testSession || ({} as TestSessionWithoutMembersWithResult_testSession),
    [testSessionDetail?.testSession]
  )
  const formattedSessionStartDate = React.useMemo(
    () => (sessionStartDate ? getFormattedDateAndTime(sessionStartDate, true) : ''),
    [sessionStartDate]
  )
  const formattedSessionEndDate = React.useMemo(
    () => (sessionEndDate ? ` - ${getFormattedDateAndTime(sessionEndDate, true, false)}` : ''),
    [sessionEndDate]
  )

  const hasPersonalSessionBooked = React.useMemo(() => {
    const user = sessionMembers?.testSessionMembers.testSessionMembers[0]
    return user?.User.id === userId && user?.isParticipant
  }, [sessionMembers?.testSessionMembers.testSessionMembers, userId])

  const { data: reportData, loading: isReportDataLoading } = useTestSessionMembersQuickReport({
    testSessionId,
    groupIds: hasCompaniesWithRole ? undefined : groups.map(({ id }) => id),
    skip: !canDownloadCsv,
  })

  const csvData = React.useMemo(
    () =>
      reportData?.testSessionMembersQuickReport.map(
        ({ firstName, lastName, email, testedAt, testResult, secondTestResult }) => ({
          'First Name': firstName,
          'Last Name': lastName,
          email,
          test_name: 'Covid19',
          last_test_date: testedAt ? format(new Date(testedAt), 'MM/dd/yyyy') : '',
          last_test_result: isInfluenza(testSessionDetail?.testSession?.type)
            ? `A: ${testResult ?? ''} | B: ${secondTestResult ?? ''}`
            : testResult,
        })
      ) ?? [],
    [reportData?.testSessionMembersQuickReport, testSessionDetail?.testSession?.type]
  )

  const isInsurance = React.useMemo(
    () => testSessionDetail?.testSession?.paymentStatus === TestSessionPaymentStatus.Insurance,
    [testSessionDetail?.testSession]
  )

  const insuranceCountCsvData = React.useMemo(() => {
    const insuranceCompanies: String[] = []
    reportData?.testSessionMembersQuickReport.forEach((member) => {
      if (
        member.insuranceCompany &&
        member.testResult &&
        member.testResult !== TestResult.Unsatisfactory
      ) {
        insuranceCompanies.push(member.insuranceCompany)
      }
    })

    const insuranceCompaniesCount = insuranceCompanies.reduce((acc, curr) => {
      if (acc[curr]) {
        acc[curr]++
      } else {
        acc[curr] = 1
      }
      return acc
    }, {} as { [key: string]: number })

    const results: { Insurance: string; Count: Number }[] = []

    Object.entries(insuranceCompaniesCount).forEach(([key, value]) => {
      results.push({ Insurance: key, Count: value })
    })

    return results
  }, [reportData?.testSessionMembersQuickReport])

  const sessionMembersCount = sessionMembers?.testSessionMembers.pagination.total ?? 0
  const sessionMembersPageLength = sessionMembers?.testSessionMembers.pagination.length ?? 0

  const canSeeResult = React.useCallback(
    (
      User: TestSessionMembers_testSessionMembers_testSessionMembers_User,
      Group: TestSessionMembers_testSessionMembers_testSessionMembers_Group | null
    ) =>
      !!(
        !!User?.GroupUsers?.filter((group) => group?.groupId === Group?.id && group?.acceptedAt)
          .length ||
        User.id === userId ||
        !!subpersonData?.subperson?.filter((subperson) => User.id === subperson?.id)
      ),
    [subpersonData?.subperson, userId]
  )

  const openDialogDeleteMember = ({
    id,
    name,
    member,
  }: {
    member: keyof typeof MemberType
    name: string
    id: string
  }) => {
    setRowId(id)
    setMemberSelected({
      id,
      name,
      member,
    })
    setConfirmDeleteMember(true)
  }

  const handleRemoveTestSessionMember = React.useCallback(async () => {
    try {
      await removeTestSessionMember({
        variables: {
          inputData: {
            id: memberSelected!.id,
          },
        },
      })
      setToastMessage('Member successfully deleted!', 'success')
      setConfirmDeleteMember(false)
      setMemberSelected(null)
      await refetchSessionMembers()
    } catch {
      // ignore
    }
    setRowId(null)
  }, [memberSelected, removeTestSessionMember, setToastMessage, refetchSessionMembers])

  const onCheckSelected = (checked: boolean, id: string) => {
    setSelected((prev) => (checked ? [...prev, id] : prev.filter((pv) => pv !== id)))
  }

  const onCheckSelectedAll = (checked: boolean, ids: string[]) => {
    if (!checked) {
      setAllSelected(false)
    }
    setSelected((prev) =>
      checked
        ? [...prev, ...ids].reduce((acc, curr) => {
            if (acc.some((a) => a === curr)) return acc
            return [...acc, curr]
          }, [] as string[])
        : []
    )
  }

  const onDeleteMembers = async () => {
    const payload = {
      ids: allSelected ? [] : selected,
      testSessionId,
      removeAll: allSelected,
    }

    await deleteMember({
      variables: payload,
    })
  }

  const groupsUserWithRoles = testSessionDetail?.testSession?.Groups.filter((group) =>
    groups.map((group) => group.id).includes(group.id)
  ).map((group) => group.id)

  const isPatientOrLiaisonWithPermission = React.useMemo(
    () =>
      (hasRoles([UserRole.LIAISON]) && hasGroupsWithRole) ||
      (hasRoles([UserRole.PATIENT]) && (hasGroupsWithRole || hasCompaniesWithRole)),
    [hasCompaniesWithRole, hasGroupsWithRole, hasRoles]
  )

  const canAddPadients = React.useMemo(
    () => isPatientOrLiaisonWithPermission && !testSessionDetail?.testSession?.finishedAt,
    [isPatientOrLiaisonWithPermission, testSessionDetail?.testSession?.finishedAt]
  )

  return (
    <>
      <Details
        topContent={
          <Datalist
            isLoading={isSessionDetailLoading}
            skeletonProps={{
              columns: 5,
            }}
            data={
              testSessionDetail
                ? [
                    {
                      date: `${formattedSessionStartDate} ${formattedSessionEndDate}`,
                      location: testSessionDetail?.testSession?.Location,
                      numberOfPeople,
                      isFinished: testSessionDetail?.testSession?.finishedAt,
                      paymentStatus: testSessionDetail?.testSession?.paymentStatus,
                      invoiceDocNumber: testSessionDetail?.testSession?.Invoice?.invoiceDocNumber,
                      invoiceId: testSessionDetail?.testSession?.Invoice?.invoiceId,
                    },
                  ]
                : []
            }
            header={
              <Datalist.Title
                rightContent={
                  <>
                    {isEditable && !isSessionScheduled && !isSessionFinished && (
                      <Button onClick={() => setIsEditDialogOpen(true)}>Edit session</Button>
                    )}
                    {canDownloadCsv && isInsurance && (
                      <Loading isLoading={isReportDataLoading}>
                        <InsuranceCountDownloadButton
                          csvData={insuranceCountCsvData}
                          testSessionName={testSessionName}
                        />
                      </Loading>
                    )}
                    {canDownloadCsv && (
                      <Loading isLoading={isReportDataLoading}>
                        <CsvDownloadButton csvData={csvData} testSessionName={testSessionName} />
                      </Loading>
                    )}
                    {hasPersonalSessionBooked && <ShowUserCode isBarcode userId={userId!} />}
                    {!isNull(columnLabels.paymentStatus) &&
                      testSessionDetail?.testSession?.paymentStatus === 'Pending' && (
                        <Button
                          onClick={() => {
                            setIsDialogOpen(true)
                          }}
                        >
                          Session payment
                        </Button>
                      )}
                    {isOrderVisible && !!testSessionDetail?.testSession?.Products.length && (
                      <OrderedProductsInfo
                        priceOfAllProducts={testSessionDetail.testSession.totalPrice!}
                        products={testSessionDetail.testSession.Products}
                      />
                    )}
                  </>
                }
                leftContent={
                  <>
                    <Button
                      size="small"
                      colorVariant="secondary"
                      appearance="link"
                      to={goBackPath}
                      ml="auto"
                    >
                      <Icon.ChevronLeft size="XXL" />
                    </Button>
                    <Text fontSize="xl">Testing session</Text>
                  </>
                }
              />
            }
            shouldRenderTableHeader={isDesktop}
            renderTableHeader={() => (
              <Datalist.Row gridTemplateColumns={rowGridTemplateColumns}>
                {!isNull(columnLabels.date) ? (
                  <Datalist.HeaderCell label={columnLabels.date} />
                ) : null}
                {!isNull(columnLabels.location) ? (
                  <Datalist.HeaderCell label={columnLabels.location} />
                ) : null}
                {!isNull(columnLabels.numOfPeople) ? (
                  <Datalist.HeaderCell label={columnLabels.numOfPeople} />
                ) : null}
                {!isNull(columnLabels.status) ? (
                  <Datalist.HeaderCell label={columnLabels.status} />
                ) : null}
                {!isNull(columnLabels.paymentStatus) ? (
                  <Datalist.HeaderCell label={columnLabels.paymentStatus} />
                ) : null}
              </Datalist.Row>
            )}
            renderTableRow={({
              date,
              location,
              numberOfPeople,
              isFinished,
              paymentStatus,
              invoiceDocNumber,
              invoiceId,
            }) => (
              <Datalist.Row gridTemplateColumns={rowGridTemplateColumns}>
                {!isNull(columnLabels.date) ? (
                  <Datalist.Cell label="Date">{date}</Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.location) ? (
                  <Datalist.Cell label={columnLabels.location}>
                    {location && getFormattedAddress(location)}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.numOfPeople) ? (
                  <Datalist.Cell label={columnLabels.numOfPeople}>{numberOfPeople}</Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.status) ? (
                  <Datalist.Cell label={columnLabels.status}>
                    {isFinished ? (
                      <Label indicator="green">Finished</Label>
                    ) : (
                      <Label indicator="yellow">In progress</Label>
                    )}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.paymentStatus) ? (
                  <Datalist.Cell label={columnLabels.paymentStatus}>
                    {paymentStatus !== undefined && (
                      <TestSessionPaymentLabel
                        paymentStatus={paymentStatus}
                        invoices={invoiceDocNumber ?? invoiceId}
                        isInvoiceDocNumber={!!invoiceDocNumber}
                      />
                    )}
                  </Datalist.Cell>
                ) : null}
              </Datalist.Row>
            )}
          />
        }
      >
        <Card>
          <Datalist
            isLoading={areSessionMembersLoading || isRefetchingSessionMembers}
            skeletonProps={{
              columns: 3,
            }}
            data={sessionMembers?.testSessionMembers.testSessionMembers ?? []}
            header={
              <Datalist.Title
                rightContent={
                  <>
                    {!!selected.length && hasPermission([UserPermissions.SEE_BULK_DELETE]) && (
                      <Button
                        appearance="ghost"
                        size="small"
                        onClick={() => setConfirmDeletePerson(true)}
                        isFetching={isLoading}
                      >
                        Delete Selected Patients
                      </Button>
                    )}
                    {!!selected.length &&
                      !allSelected &&
                      MemberCount !== selected.length &&
                      hasPermission([UserPermissions.SEE_BULK_DELETE]) && (
                        <Button
                          appearance="ghost"
                          size="small"
                          onClick={() => setAllSelected(true)}
                        >
                          Select all {MemberCount} patients
                        </Button>
                      )}
                    {canAddPadients && (
                      <AddNewTestSessionMembers
                        companyId={testSessionDetail?.testSession?.Company?.id}
                        buttonLabel="Add patient"
                        testSessionId={testSessionId}
                        addMemberType="participant"
                        onAdd={() => void refetchSessionMembers()}
                        groupId={
                          testSessionDetail?.testSession?.Group?.id ??
                          (groupsUserWithRoles && groupsUserWithRoles[0])
                        }
                      />
                    )}
                    <TextInput
                      width={isMobile ? '100%' : pxToRem(200)}
                      value={emailOrNameLike}
                      onChange={(event) => {
                        setEmailOrNameLike(event.target.value)
                      }}
                      placeholder="Search"
                      rightIcon={<Icon.Search size="L" color={COLOR.gray} />}
                      hasBottomContent={false}
                      hasTopContent={false}
                    />
                    {isSessionFinished && (
                      <SelectInput
                        width={isMobile ? '100%' : pxToRem(175)}
                        value={searchState.data.tested ?? ''}
                        options={[
                          {
                            label: 'All',
                            value: TestedType.ALL,
                          },
                          {
                            label: 'All Tested',
                            value: TestedType.TESTED_YET,
                          },
                          {
                            label: 'Not Tested',
                            value: TestedType.NOT_TESTED_YET,
                          },
                        ]}
                        onChange={(event) => {
                          searchState.setSearchState(
                            { tested: event.target.value, page: 1 },
                            false,
                            true
                          )
                        }}
                        hasBottomContent={false}
                        hasTopContent={false}
                      />
                    )}
                    {isAddable && (
                      <AddNewTestSessionMembers
                        hasGroupRoleOnly={!!groupIds}
                        companyId={testSessionDetail?.testSession?.Company?.id}
                        isSearchingInCompanyOnly
                        buttonLabel="Add patient"
                        buttonColorVariant="primary"
                        testSessionId={testSessionId}
                        addMemberType="participant"
                        onAdd={async () => {
                          setIsRefetchingSessionMembers(true)
                          await refetchSessionMembers()
                          setIsRefetchingSessionMembers(false)
                        }}
                      />
                    )}
                  </>
                }
              >
                Patient list
              </Datalist.Title>
            }
            sorter={sorter}
            onSortChange={handleSortChange}
            shouldRenderTableHeader={isDesktop}
            renderTableHeader={() => (
              <Datalist.Row gridTemplateColumns="repeat(5, 1fr)">
                <Datalist.HeaderCell label="">
                  {!!patientsNotTested.length && hasPermission([UserPermissions.SEE_BULK_DELETE]) && (
                    <Checkbox
                      label=""
                      checked={selected.length >= patientsNotTested.length || allSelected}
                      onChange={(e) =>
                        onCheckSelectedAll(
                          e.target.checked,
                          patientsNotTested.map((patient) => patient.id)
                        )
                      }
                    />
                  )}
                </Datalist.HeaderCell>
                <Datalist.HeaderCell sortKey={SortByType.USER_DOT_LASTNAME} label="name" />
                <Datalist.HeaderCell sortKey={SortByType.USER_DOT_EMAIL} label="email" />
                <Datalist.HeaderCell label="result" />
              </Datalist.Row>
            )}
            renderTableRow={({ User, Test, id, Group }) => (
              <Datalist.Row
                {...(params.companyId && {
                  onClick: () => {
                    history.push(
                      `/${ROUTES.COMPANY}/${params.companyId}/${COMPANY_ROUTES.MEMBERS}/${User.id}`,
                      {
                        from: COMPANY_ROUTES.TEST_SESSIONS,
                      }
                    )
                  },
                })}
                gridTemplateColumns="repeat(5, 1fr)"
              >
                <Datalist.Cell label="" onClick={(e) => e.stopPropagation()}>
                  {!Test?.testedAt && hasPermission([UserPermissions.SEE_BULK_DELETE]) && (
                    <Checkbox
                      label=""
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => {
                        e.stopPropagation()
                        onCheckSelected(e.target.checked, id)
                      }}
                      checked={!!selected.find((s) => s === id) || allSelected}
                    />
                  )}
                </Datalist.Cell>
                <Datalist.Cell label="name">
                  {User.firstName} {User.lastName}
                </Datalist.Cell>
                <Datalist.Cell label="email">
                  {User?.email || <Label indicator="yellow">Subperson</Label>}
                </Datalist.Cell>
                <Datalist.Cell label="result">
                  {canSeeResult(User, Group) ? (
                    <>
                      {!Test ? (
                        <Label indicator="red">Not tested yet</Label>
                      ) : (
                        <>
                          <Label
                            indicator={Test?.result ? getResultLabelColor(Test?.result) : 'blue'}
                          >
                            {isInfluenza(testSessionDetail?.testSession?.type) && `A: `}
                            {Test?.result || 'Pending result'}
                          </Label>
                          {isInfluenza(testSessionDetail?.testSession?.type) &&
                            Test?.secondResult && (
                              <Label indicator={getResultLabelColor(Test?.secondResult)}>
                                {`B: ${Test?.secondResult}`}
                              </Label>
                            )}
                        </>
                      )}
                      {!!Test?.result && !!Test?.id && (
                        <div>
                          <DownloadTestResultPdfButton
                            testId={Test.id}
                            appearance="ghost"
                            size="small"
                          />
                        </div>
                      )}
                    </>
                  ) : (
                    <Box display="grid">
                      <Text fontSize="xs" color={COLOR.grayDark}>
                        Pending user authorization
                      </Text>
                    </Box>
                  )}
                </Datalist.Cell>
                {!Test?.testedAt &&
                !testSessionDetail?.testSession?.finishedAt &&
                (hasPermission([
                  UserPermissions.REMOVE_PATIENT_TEST_SESSION,
                  UserPermissions.REMOVE_PERSON_TEST_SESSION,
                ]) ||
                  hasCompaniesWithRole ||
                  hasGroupsWithRole) ? (
                  <Datalist.Cell>
                    <Button
                      onClick={(e) => {
                        e.stopPropagation()
                        void openDialogDeleteMember({
                          member: 'participant',
                          name: `${User.firstName || ''} ${User.lastName || ''}`,
                          id,
                        })
                      }}
                      appearance="ghost"
                      colorVariant="danger"
                      size="small"
                    >
                      {rowId === testSessionDetail?.testSession?.id &&
                      isLoadingRemoveTestSessionMember ? (
                        <Spinner color={COLOR.danger} size="small" />
                      ) : (
                        <Icon.Trash />
                      )}
                    </Button>
                  </Datalist.Cell>
                ) : null}
              </Datalist.Row>
            )}
          />
        </Card>
        {sessionMembersCount > sessionMembersPageLength && (
          <Pagination.Wrapper>
            <Pagination
              firstPage={1}
              lastPage={Math.ceil(sessionMembersCount / sessionMembersPageLength)}
              currentPage={Number(searchState.data.page ?? 1)}
              onPageChange={(page) => {
                searchState.setSearchState({ page }, false)
              }}
            />
          </Pagination.Wrapper>
        )}
      </Details>
      {isDialogOpen && testSessionDetail && (
        <SessionPaymentDialog
          testSessionId={testSessionDetail.testSession!.id}
          onDismiss={() => {
            setIsDialogOpen(false)
          }}
          onSuccess={() => {
            void refetchSessionDetail()
            setIsDialogOpen(false)
          }}
        />
      )}
      {testSessionDetail?.testSession && (
        <EditSession
          session={testSessionDetail.testSession}
          isEditing={isEditDialogOpen}
          onCloseDialog={() => {
            setIsEditDialogOpen(false)
          }}
          onSuccess={async () => {
            setToastMessage('Test Session successfully edited!', 'success')
            await refetchSessionDetail()
            setIsEditDialogOpen(false)
          }}
        />
      )}

      <ConfirmationDialog
        title="Deleting Members from Session"
        isOpen={confirmDeletePerson}
        onDismiss={() => setConfirmDeletePerson(false)}
        actions={
          <>
            <Button onClick={() => setConfirmDeletePerson(false)} colorVariant="secondary">
              Cancel
            </Button>
            <Button onClick={onDeleteMembers} isFetching={isLoading}>
              Delete All
            </Button>
          </>
        }
      >
        <Box display="flex" flexDirection="column">
          <Text>Are you sure you want to remove these members from this Test Session?</Text>
        </Box>
      </ConfirmationDialog>

      <ConfirmationDialog
        title="Deleting Member from Session"
        isOpen={confirmDeleteMember}
        onDismiss={() => setConfirmDeleteMember(false)}
        actions={
          <>
            <Button onClick={() => setConfirmDeleteMember(false)} colorVariant="secondary">
              Cancel
            </Button>
            <Button
              onClick={handleRemoveTestSessionMember}
              isFetching={isLoadingRemoveTestSessionMember}
              disabled={isLoadingRemoveTestSessionMember}
            >
              Delete
            </Button>
          </>
        }
      >
        <Box display="flex" flexDirection="column">
          <Text>
            Are you sure you want to remove {memberSelected?.name || 'member'} from this Test
            Session?
          </Text>
        </Box>
      </ConfirmationDialog>
    </>
  )
}
